import { FC, useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
    Box, Tabs, Tab, Typography, Button, IconButton, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, MenuItem, FormControlLabel, Switch, Paper,
    Drawer, Grid, Divider, Tooltip, Menu
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import TableChartIcon from '@mui/icons-material/TableChart';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WeekendIcon from '@mui/icons-material/Weekend';
import LoopIcon from '@mui/icons-material/Loop';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CustomDataGrid from '../../components/DataGrid/DataGrid';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { toast } from 'react-toastify';
import moment from 'moment';
import {
    HolidayImportDialog,
    downloadHolidayExcelTemplate,
    downloadHolidayCsvTemplate,
    exportHolidaysToExcel
} from '../../components/Employee/HolidayImportDialog';

import {
    fetchLeaveConfigs, createLeaveConfig, updateLeaveConfig, deleteLeaveConfig,
    fetchShifts, createShift, updateShift, deleteShift,
    fetchHolidays, createHoliday, updateHoliday, deleteHoliday,
    fetchWeekOffs, createWeekOff, updateWeekOff, deleteWeekOff,
    fetchShiftConfigs, createShiftConfig, updateShiftConfig, deleteShiftConfig,
    assignShiftPatternToEmployee, assignWeekOffToEmployee, fetchEmployeesByAccount,
    fetchWorkLocations, createWorkLocation, updateWorkLocation, deleteWorkLocation,
} from '../../store/hrmsStore/hrmsThunk';
import { fetchAccountById, updateAccount } from '../../store/accountThunk';
import { fetchAddresses } from '../../store/addressStore/addressThunk';
import DialogAddressForm from '../../components/DialogAddressForm/DialogAddressForm';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';

const HrmsEmptyState: FC<{ icon: any; title: string; description: string; buttonLabel?: string; onAction?: () => void }> = ({ icon: Icon, title, description, buttonLabel, onAction }) => (
    <Paper
        elevation={0}
        sx={{
            height: '70vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#fafafb',
            border: '2px dashed #e0e0e0',
            borderRadius: 2,
            p: 4,
            mr: '10px',
            mt: 1
        }}
    >
        <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
            <Box sx={{ width: 120, height: 120, borderRadius: '50%', backgroundColor: 'primary.light', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', mb: 3 }}>
                <Icon sx={{ fontSize: 60, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>{title}</Typography>
            <Typography variant="body1" sx={{ color: '#666', mb: 4, lineHeight: 1.6 }}>{description}</Typography>
            {buttonLabel && onAction && (
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<AddIcon />}
                    onClick={onAction}
                    sx={{
                        px: 4, py: 2, borderRadius: 3, textTransform: 'none', fontSize: '16px', fontWeight: 600,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': { transform: 'translateY(-4px) scale(1.02)', boxShadow: (theme: any) => `0 8px 25px -8px ${theme.palette.primary.main}` },
                        '&:active': { transform: 'translateY(-2px) scale(0.98)' },
                    }}
                >
                    {buttonLabel}
                </Button>
            )}
        </Box>
    </Paper>
);

const formatShiftTime = (value?: string) => {
    if (!value) return '-';

    const parsed = moment(value, ['HH:mm', 'HH:mm:ss'], true);
    return parsed.isValid() ? parsed.format('hh:mm A') : value;
};

// ===================== LEAVE CONFIG TAB =====================
const LeaveConfigTab: FC = () => {
    const dispatch = useAppDispatch();
    const { selectedAccount } = useAppSelector(state => state.auth);
    const [configs, setConfigs] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editData, setEditData] = useState<any>(null);
    const [form, setForm] = useState({
        name: '', code: '', description: '', allocationType: 'yearly',
        yearlyAllocation: 0, monthlyAllocation: 0, carryForward: false,
        maxCarryForward: 0, canEncash: false
    });

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await dispatch(fetchLeaveConfigs(selectedAccount._id)).unwrap();
            setConfigs(res?.data || res || []);
        } catch { setConfigs([]); }
        setLoading(false);
    };

    useEffect(() => { loadData(); }, [selectedAccount]);

    const handleOpen = (data?: any) => {
        if (data) {
            setEditData(data);
            setForm({
                name: data.name, code: data.code, description: data.description,
                allocationType: data.allocationType, yearlyAllocation: data.yearlyAllocation || 0,
                monthlyAllocation: data.monthlyAllocation || 0, carryForward: data.carryForward || false,
                maxCarryForward: data.maxCarryForward || 0, canEncash: data.canEncash || false
            });
        } else {
            setEditData(null);
            setForm({ name: '', code: '', description: '', allocationType: 'yearly', yearlyAllocation: 0, monthlyAllocation: 0, carryForward: false, maxCarryForward: 0, canEncash: false });
        }
        setDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            if (editData) {
                await dispatch(updateLeaveConfig({ configId: editData._id || editData.id, data: form })).unwrap();
                toast.success('Leave config updated');
            } else {
                await dispatch(createLeaveConfig({ accountId: selectedAccount._id, data: { ...form, isActive: true } })).unwrap();
                toast.success('Leave config created');
            }
            setDialogOpen(false);
            loadData();
        } catch (err: any) { toast.error(err?.message || 'Failed'); }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Delete this leave config?')) return;
        try {
            await dispatch(deleteLeaveConfig(id)).unwrap();
            toast.success('Deleted');
            loadData();
        } catch (err: any) { toast.error(err?.message || 'Failed'); }
    };

    const columns = [
        { field: 'name', headerName: 'Name', flex: 1, minWidth: 120 },
        { field: 'code', headerName: 'Code', flex: 0.6, minWidth: 80 },
        { field: 'allocationType', headerName: 'Type', flex: 0.7, minWidth: 90,
            renderCell: ({ value }: any) => <Chip label={value} size="small" style={{ color: '#488dff', backgroundColor: '#f1f0fc', border: '1px solid #488dff' }} />
        },
        { field: 'yearlyAllocation', headerName: 'Yearly', flex: 0.5, minWidth: 70 },
        { field: 'monthlyAllocation', headerName: 'Monthly', flex: 0.5, minWidth: 70 },
        { field: 'carryForward', headerName: 'Carry Forward', flex: 0.7, minWidth: 100,
            renderCell: ({ value }: any) => value
                ? <Chip label="Yes" size="small" style={{ color: '#49a563', backgroundColor: '#edf6ef', border: '1px solid #49a563' }} />
                : <Chip label="No" size="small" style={{ color: '#c33b3b', backgroundColor: '#fbecec', border: '1px solid #c33b3b' }} />
        },
        { field: 'canEncash', headerName: 'Encash', flex: 0.5, minWidth: 80,
            renderCell: ({ value }: any) => value
                ? <Chip label="Yes" size="small" style={{ color: '#49a563', backgroundColor: '#edf6ef', border: '1px solid #49a563' }} />
                : <Chip label="No" size="small" style={{ color: '#c33b3b', backgroundColor: '#fbecec', border: '1px solid #c33b3b' }} />
        },
        { field: 'actions', headerName: 'Actions', flex: 0.7, minWidth: 100,
            renderCell: ({ row }: any) => (
                <>
                    <IconButton style={{ color: '#49a563', backgroundColor: '#edf6ef', border: '0.1px solid #49a563', height: 35, width: 35, marginRight: 10 }}
                        size="small" onClick={() => handleOpen(row)}><EditIcon style={{ fontSize: 16 }} /></IconButton>
                    <IconButton style={{ color: '#c33b3b', backgroundColor: '#fbecec', border: '0.1px solid #c33b3b', height: 35, width: 35 }}
                        size="small" onClick={() => handleDelete(row._id || row.id)}><DeleteIcon style={{ fontSize: 16 }} /></IconButton>
                </>
            )
        }
    ];

    return (
        <>
            {document.getElementById('hrms-actions') && createPortal(
                <Button startIcon={<AddIcon />} size="small" onClick={() => handleOpen()}
                    style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7 }}>
                    Add Leave Type
                </Button>,
                document.getElementById('hrms-actions')!
            )}
            {!loading && configs.length === 0 ? (
                <HrmsEmptyState icon={EventNoteIcon} title="No Leave Types Yet" description="Configure leave types for your employees. Set up casual leave, sick leave, earned leave, and more." buttonLabel="Add Leave Type" onAction={() => handleOpen()} />
            ) : (
                <div style={{ height: '77vh', width: '100%', overflow: 'auto', paddingRight: 10 }}>
                    <CustomDataGrid columns={columns} rows={configs} getRowId={(row: any) => row._id || row.id} loading={loading} />
                </div>
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editData ? 'Edit' : 'Add'} Leave Config</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="Name" size="small" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} fullWidth />
                        <TextField label="Code" size="small" value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} fullWidth />
                        <TextField label="Description" size="small" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} fullWidth multiline rows={2} />
                        <TextField label="Allocation Type" size="small" select value={form.allocationType} onChange={e => setForm({ ...form, allocationType: e.target.value })} fullWidth>
                            <MenuItem value="yearly">Yearly</MenuItem>
                            <MenuItem value="monthly">Monthly</MenuItem>
                            <MenuItem value="none">None</MenuItem>
                        </TextField>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="Yearly Allocation" size="small" type="number" value={form.yearlyAllocation || ''} onChange={e => setForm({ ...form, yearlyAllocation: e.target.value === '' ? 0 : Number(e.target.value) })} fullWidth />
                            <TextField label="Monthly Allocation" size="small" type="number" value={form.monthlyAllocation || ''} onChange={e => setForm({ ...form, monthlyAllocation: e.target.value === '' ? 0 : Number(e.target.value) })} fullWidth />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControlLabel control={<Switch checked={form.carryForward} onChange={e => setForm({ ...form, carryForward: e.target.checked })} />} label="Carry Forward" />
                            <FormControlLabel control={<Switch checked={form.canEncash} onChange={e => setForm({ ...form, canEncash: e.target.checked })} />} label="Can Encash" />
                        </Box>
                        {form.carryForward && (
                            <TextField label="Max Carry Forward" size="small" type="number" value={form.maxCarryForward || ''} onChange={e => setForm({ ...form, maxCarryForward: e.target.value === '' ? 0 : Number(e.target.value) })} fullWidth />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>{editData ? 'Update' : 'Create'}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// ===================== SHIFT TAB =====================
const ShiftTab: FC = () => {
    const dispatch = useAppDispatch();
    const [shifts, setShifts] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editData, setEditData] = useState<any>(null);
    const [form, setForm] = useState({
        name: '', shiftCode: '', shiftStartTime: '09:00', shiftEndTime: '18:00',
        canPunchIn: 'Anytime', allowPunchInBeforeHours: 0, allowPunchInBeforeMinutes: 0,
        canPunchOut: 'Anytime', allowPunchOutAfterHours: 0, allowPunchOutAfterMinutes: 0,
    });

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await dispatch(fetchShifts()).unwrap();
            setShifts(res?.data || res || []);
        } catch { setShifts([]); }
        setLoading(false);
    };

    useEffect(() => { loadData(); }, []);

    const handleOpen = (data?: any) => {
        if (data) {
            setEditData(data);
            setForm({
                name: data.name, shiftCode: data.shiftCode,
                shiftStartTime: data.shiftStartTime || '09:00', shiftEndTime: data.shiftEndTime || '18:00',
                canPunchIn: data.canPunchIn || 'Anytime', allowPunchInBeforeHours: data.allowPunchInBeforeHours || 0,
                allowPunchInBeforeMinutes: data.allowPunchInBeforeMinutes || 0,
                canPunchOut: data.canPunchOut || 'Anytime', allowPunchOutAfterHours: data.allowPunchOutAfterHours || 0,
                allowPunchOutAfterMinutes: data.allowPunchOutAfterMinutes || 0,
            });
        } else {
            setEditData(null);
            setForm({ name: '', shiftCode: '', shiftStartTime: '09:00', shiftEndTime: '18:00', canPunchIn: 'Anytime', allowPunchInBeforeHours: 0, allowPunchInBeforeMinutes: 0, canPunchOut: 'Anytime', allowPunchOutAfterHours: 0, allowPunchOutAfterMinutes: 0 });
        }
        setDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            if (editData) {
                await dispatch(updateShift({ id: editData._id || editData.id, data: form })).unwrap();
                toast.success('Shift updated');
            } else {
                await dispatch(createShift({ ...form, isActive: true })).unwrap();
                toast.success('Shift created');
            }
            setDialogOpen(false);
            loadData();
        } catch (err: any) { toast.error(err?.message || 'Failed'); }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Delete this shift?')) return;
        try {
            await dispatch(deleteShift(id)).unwrap();
            toast.success('Deleted');
            loadData();
        } catch (err: any) { toast.error(err?.message || 'Failed'); }
    };

    const columns = [
        { field: 'name', headerName: 'Shift Name', flex: 1, minWidth: 130 },
        { field: 'shiftCode', headerName: 'Code', flex: 0.6, minWidth: 80 },
        {
            field: 'shiftStartTime',
            headerName: 'Start Time',
            flex: 0.7,
            minWidth: 110,
            renderCell: ({ value }: any) => formatShiftTime(value)
        },
        {
            field: 'shiftEndTime',
            headerName: 'End Time',
            flex: 0.7,
            minWidth: 110,
            renderCell: ({ value }: any) => formatShiftTime(value)
        },
        { field: 'canPunchIn', headerName: 'Punch In', flex: 0.7, minWidth: 90 },
        { field: 'canPunchOut', headerName: 'Punch Out', flex: 0.7, minWidth: 90 },
        { field: 'actions', headerName: 'Actions', flex: 0.7, minWidth: 110,
            renderCell: ({ row }: any) => (
                <>
                    <IconButton style={{ color: '#49a563', backgroundColor: '#edf6ef', border: '0.1px solid #49a563', height: 35, width: 35, marginRight: 10 }}
                        size="small" onClick={() => handleOpen(row)}><EditIcon style={{ fontSize: 16 }} /></IconButton>
                    <IconButton style={{ color: '#c33b3b', backgroundColor: '#fbecec', border: '0.1px solid #c33b3b', height: 35, width: 35 }}
                        size="small" onClick={() => handleDelete(row._id || row.id)}><DeleteIcon style={{ fontSize: 16 }} /></IconButton>
                </>
            )
        }
    ];

    return (
        <>
            {document.getElementById('hrms-actions') && createPortal(
                <Button startIcon={<AddIcon />} size="small" onClick={() => handleOpen()}
                    style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7 }}>
                    Add Shift
                </Button>,
                document.getElementById('hrms-actions')!
            )}
            {!loading && shifts.length === 0 ? (
                <HrmsEmptyState icon={AccessTimeIcon} title="No Shifts Yet" description="Define work shifts for your organization. Set up morning, evening, night shifts with punch-in/out timings." buttonLabel="Add Shift" onAction={() => handleOpen()} />
            ) : (
                <div style={{ height: '77vh', width: '100%', overflow: 'auto', paddingRight: 10 }}>
                    <CustomDataGrid columns={columns} rows={shifts} getRowId={(row: any) => row._id || row.id} loading={loading} />
                </div>
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editData ? 'Edit' : 'Add'} Shift</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="Shift Name" size="small" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} fullWidth />
                        <TextField label="Shift Code" size="small" value={form.shiftCode} onChange={e => setForm({ ...form, shiftCode: e.target.value.toUpperCase() })} fullWidth />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="Start Time" size="small" type="time" value={form.shiftStartTime} onChange={e => setForm({ ...form, shiftStartTime: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
                            <TextField label="End Time" size="small" type="time" value={form.shiftEndTime} onChange={e => setForm({ ...form, shiftEndTime: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="Punch In Rule" size="small" select value={form.canPunchIn} onChange={e => setForm({ ...form, canPunchIn: e.target.value })} fullWidth>
                                <MenuItem value="Anytime">Anytime</MenuItem>
                                <MenuItem value="Add Limit">Add Limit</MenuItem>
                            </TextField>
                            <TextField label="Punch Out Rule" size="small" select value={form.canPunchOut} onChange={e => setForm({ ...form, canPunchOut: e.target.value })} fullWidth>
                                <MenuItem value="Anytime">Anytime</MenuItem>
                                <MenuItem value="Limit">Limit</MenuItem>
                            </TextField>
                        </Box>
                        {form.canPunchIn === 'Add Limit' && (
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField label="Before Hours" size="small" type="number" value={form.allowPunchInBeforeHours || ''} onChange={e => setForm({ ...form, allowPunchInBeforeHours: e.target.value === '' ? 0 : Number(e.target.value) })} fullWidth />
                                <TextField label="Before Minutes" size="small" type="number" value={form.allowPunchInBeforeMinutes || ''} onChange={e => setForm({ ...form, allowPunchInBeforeMinutes: e.target.value === '' ? 0 : Number(e.target.value) })} fullWidth />
                            </Box>
                        )}
                        {form.canPunchOut === 'Limit' && (
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField label="After Hours" size="small" type="number" value={form.allowPunchOutAfterHours || ''} onChange={e => setForm({ ...form, allowPunchOutAfterHours: e.target.value === '' ? 0 : Number(e.target.value) })} fullWidth />
                                <TextField label="After Minutes" size="small" type="number" value={form.allowPunchOutAfterMinutes || ''} onChange={e => setForm({ ...form, allowPunchOutAfterMinutes: e.target.value === '' ? 0 : Number(e.target.value) })} fullWidth />
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>{editData ? 'Update' : 'Create'}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// ===================== HOLIDAY TAB =====================
const HolidayTab: FC = () => {
    const dispatch = useAppDispatch();
    const [holidays, setHolidays] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [templateAnchorEl, setTemplateAnchorEl] = useState<null | HTMLElement>(null);
    const [editData, setEditData] = useState<any>(null);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const currentYear = new Date().getFullYear();
    const [allHolidays, setAllHolidays] = useState<any[]>([]);
    const [year, setYear] = useState<number | 'ALL'>(currentYear);
    const [form, setForm] = useState({
        name: '', date: '', country: 'India', state: '', holidayType: 'Mandatory',
        locationCode: '', locationInfo: ''
    });

    // Year options come from the data itself (whatever years have holidays) + current year
    const yearOptions = useMemo(() => {
        const years = new Set<number>([currentYear]);
        allHolidays.forEach(h => { const y = moment(h.date).year(); if (!isNaN(y)) years.add(y); });
        return Array.from(years).sort((a, b) => a - b);
    }, [allHolidays, currentYear]);

    // Load everything once; year filter is applied client-side
    const loadData = async () => {
        setLoading(true);
        try {
            const res = await dispatch(fetchHolidays({ year: 'ALL' })).unwrap();
            setAllHolidays(res?.data || res || []);
        } catch { setAllHolidays([]); }
        setLoading(false);
    };

    useEffect(() => { loadData(); }, []);

    useEffect(() => {
        setHolidays(year === 'ALL' ? allHolidays : allHolidays.filter(h => moment(h.date).year() === year));
    }, [allHolidays, year]);

    // Same calendar date + same state + same location = duplicate (checked against ALL years)
    const normKey = (v: any) => (v && String(v).trim() && String(v).trim().toLowerCase() !== 'all') ? String(v).trim().toLowerCase() : '';
    const holidayKey = (h: any) => `${moment(h.date).format('YYYY-MM-DD')}|${normKey(h.state)}|${normKey(h.locationCode)}`;
    const findDuplicate = (candidate: any, excludeId?: string) =>
        allHolidays.find(h => (h._id || h.id) !== excludeId && holidayKey(h) === holidayKey(candidate));

    const handleOpen = (data?: any) => {
        if (data) {
            setEditData(data);
            setForm({
                name: data.name, date: moment(data.date).format('YYYY-MM-DD'),
                country: data.country || 'India', state: data.state || '',
                holidayType: data.holidayType || 'Mandatory',
                locationCode: data.locationCode || '', locationInfo: data.locationInfo || ''
            });
        } else {
            setEditData(null);
            setForm({ name: '', date: '', country: 'India', state: '', holidayType: 'Mandatory', locationCode: '', locationInfo: '' });
        }
        setDialogOpen(true);
    };

    const handleSave = async () => {
        const dup = findDuplicate(form, editData ? (editData._id || editData.id) : undefined);
        if (dup) {
            toast.error(`A holiday already exists on ${moment(form.date).format('DD MMM YYYY')}: "${dup.name}"`);
            return;
        }
        try {
            if (editData) {
                await dispatch(updateHoliday({ id: editData._id || editData.id, data: form })).unwrap();
                toast.success('Holiday updated');
            } else {
                await dispatch(createHoliday({ ...form, isActive: true })).unwrap();
                toast.success('Holiday created');
            }
            setDialogOpen(false);
            loadData();
        } catch (err: any) { toast.error(err?.message || 'Failed'); }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Delete this holiday?')) return;
        try {
            await dispatch(deleteHoliday(id)).unwrap();
            toast.success('Deleted');
            loadData();
        } catch (err: any) { toast.error(err?.message || 'Failed'); }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        if (!window.confirm(`Delete ${selectedIds.length} selected holidays?`)) return;
        setLoading(true);
        let success = 0, fail = 0;
        for (const id of selectedIds) {
            try { await dispatch(deleteHoliday(id)).unwrap(); success++; } catch { fail++; }
        }
        toast.success(`${success} deleted${fail > 0 ? `, ${fail} failed` : ''}`);
        setSelectedIds([]);
        loadData();
    };

    // Delete every holiday currently loaded for the selected year
    const handleDeleteYear = async () => {
        if (year === 'ALL' || holidays.length === 0) return;
        if (!window.confirm(`Delete ALL ${holidays.length} holidays of ${year}? This cannot be undone.`)) return;
        setLoading(true);
        let success = 0, fail = 0;
        for (const h of holidays) {
            try { await dispatch(deleteHoliday(h._id || h.id)).unwrap(); success++; } catch { fail++; }
        }
        toast.success(`${success} holidays of ${year} deleted${fail > 0 ? `, ${fail} failed` : ''}`);
        setSelectedIds([]);
        loadData();
    };

    const columns = [
        { field: 'name', headerName: 'Holiday Name', flex: 1.2, minWidth: 150 },
        { field: 'date', headerName: 'Date', flex: 0.8, minWidth: 110,
            valueGetter: ({ row }: any) => moment(row.date).format('DD MMM YYYY')
        },
        { field: 'holidayType', headerName: 'Type', flex: 0.7, minWidth: 100,
            renderCell: ({ value }: any) => {
                const style = value === 'Mandatory'
                    ? { color: '#c33b3b', backgroundColor: '#fbecec', border: '1px solid #c33b3b' }
                    : value === 'Optional'
                    ? { color: '#e6a817', backgroundColor: '#fdf8e8', border: '1px solid #e6a817' }
                    : { color: '#488dff', backgroundColor: '#f1f0fc', border: '1px solid #488dff' };
                return <Chip label={value} size="small" style={style} />;
            }
        },
        { field: 'country', headerName: 'Country', flex: 0.6, minWidth: 80 },
        { field: 'state', headerName: 'State', flex: 0.6, minWidth: 80 },
        { field: 'actions', headerName: 'Actions', flex: 0.7, minWidth: 110,
            renderCell: ({ row }: any) => (
                <>
                    <IconButton style={{ color: '#49a563', backgroundColor: '#edf6ef', border: '0.1px solid #49a563', height: 35, width: 35, marginRight: 10 }}
                        size="small" onClick={() => handleOpen(row)}><EditIcon style={{ fontSize: 16 }} /></IconButton>
                    <IconButton style={{ color: '#c33b3b', backgroundColor: '#fbecec', border: '0.1px solid #c33b3b', height: 35, width: 35 }}
                        size="small" onClick={() => handleDelete(row._id || row.id)}><DeleteIcon style={{ fontSize: 16 }} /></IconButton>
                </>
            )
        }
    ];

    return (
        <>
            {document.getElementById('hrms-actions') && createPortal(
                <>
                    {selectedIds.length > 0 && (
                        <Button startIcon={<DeleteIcon />} size="small" onClick={handleBulkDelete}
                            style={{ color: '#c33b3b', backgroundColor: '#fff', border: '0.5px solid #c33b3b', height: 35, borderRadius: 7 }}>
                            Delete ({selectedIds.length})
                        </Button>
                    )}
                    <TextField
                        select
                        size="small"
                        value={year}
                        onChange={(e) => setYear(e.target.value === 'ALL' ? 'ALL' : Number(e.target.value))}
                        sx={{ minWidth: 120, '& .MuiInputBase-root': { height: 35, borderRadius: '7px', backgroundColor: '#fff' } }}
                    >
                        <MenuItem value="ALL">All Years</MenuItem>
                        {yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                    </TextField>
                    <Button
                        startIcon={<UploadFileIcon />}
                        endIcon={<ArrowDropDownIcon />}
                        size="small"
                        onClick={(e) => setTemplateAnchorEl(e.currentTarget)}
                        style={{ color: '#49a563', backgroundColor: '#fff', border: '0.5px solid #49a563', height: 35, borderRadius: 7 }}
                    >
                        Import / Export
                    </Button>
                    <Menu
                        anchorEl={templateAnchorEl}
                        open={Boolean(templateAnchorEl)}
                        onClose={() => setTemplateAnchorEl(null)}
                    >
                        <MenuItem onClick={() => { setImportDialogOpen(true); setTemplateAnchorEl(null); }}>
                            <UploadFileIcon fontSize="small" style={{ marginRight: 8, color: '#49a563' }} /> Import Holidays
                        </MenuItem>
                        <MenuItem disabled={holidays.length === 0} onClick={() => { exportHolidaysToExcel(holidays); setTemplateAnchorEl(null); }}>
                            <TableChartIcon fontSize="small" style={{ marginRight: 8, color: '#6366f1' }} /> Export Excel
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={() => { downloadHolidayExcelTemplate(); setTemplateAnchorEl(null); }}>
                            <FileDownloadIcon fontSize="small" style={{ marginRight: 8, color: '#0284c7' }} /> Download Excel Template (.xlsx)
                        </MenuItem>
                        <MenuItem onClick={() => { downloadHolidayCsvTemplate(); setTemplateAnchorEl(null); }}>
                            <FileDownloadIcon fontSize="small" style={{ marginRight: 8, color: '#0284c7' }} /> Download CSV Template (.csv)
                        </MenuItem>
                        <Divider />
                        <MenuItem
                            disabled={year === 'ALL' || holidays.length === 0}
                            onClick={() => { setTemplateAnchorEl(null); handleDeleteYear(); }}
                            sx={{ color: '#c33b3b' }}
                        >
                            <DeleteIcon fontSize="small" style={{ marginRight: 8 }} /> Delete all {year === 'ALL' ? '' : year} holidays{holidays.length > 0 && year !== 'ALL' ? ` (${holidays.length})` : ''}
                        </MenuItem>
                    </Menu>
                    <Button
                        startIcon={<AddIcon />}
                        size="small"
                        onClick={() => handleOpen()}
                        style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7 }}
                    >
                        Add Holiday
                    </Button>
                </>,
                document.getElementById('hrms-actions')!
            )}
            {!loading && holidays.length === 0 ? (
                <Box sx={{ mt: 1, mr: '10px' }}>
                    <HrmsEmptyState
                        icon={CalendarTodayIcon}
                        title="No Holidays Yet"
                        description="Add holidays for your organization or import them from Excel (.xlsx) / CSV (.csv) / JSON template."
                        buttonLabel="Add Holiday"
                        onAction={() => handleOpen()}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: -4, mb: 3 }}>
                        <Button
                            variant="outlined"
                            startIcon={<UploadFileIcon />}
                            onClick={() => setImportDialogOpen(true)}
                            sx={{ textTransform: 'none', color: '#49a563', borderColor: '#49a563', borderRadius: 2 }}
                        >
                            Import Excel / CSV / JSON
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<FileDownloadIcon />}
                            onClick={downloadHolidayExcelTemplate}
                            sx={{ textTransform: 'none', color: '#0284c7', borderColor: '#0284c7', borderRadius: 2 }}
                        >
                            Download Sample Template
                        </Button>
                    </Box>
                </Box>
            ) : (
                <div style={{ height: '77vh', width: '100%', overflow: 'auto', paddingRight: 10 }}>
                    <CustomDataGrid columns={columns} rows={holidays} getRowId={(row: any) => row._id || row.id} loading={loading}
                        checkboxSelection={true}
                        onSelectionModelChange={(ids: any) => setSelectedIds(ids as string[])}
                        selectionModel={selectedIds}
                    />
                </div>
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editData ? 'Edit' : 'Add'} Holiday</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="Holiday Name" size="small" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} fullWidth />
                        <TextField label="Date" size="small" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
                        <TextField label="Holiday Type" size="small" select value={form.holidayType} onChange={e => setForm({ ...form, holidayType: e.target.value })} fullWidth>
                            <MenuItem value="Mandatory">Mandatory</MenuItem>
                            <MenuItem value="Optional">Optional</MenuItem>
                            <MenuItem value="Default">Default</MenuItem>
                        </TextField>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="Country" size="small" value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} fullWidth />
                            <TextField label="State" size="small" value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} fullWidth />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="Location Code" size="small" value={form.locationCode} onChange={e => setForm({ ...form, locationCode: e.target.value })} fullWidth />
                            <TextField label="Location Info" size="small" value={form.locationInfo} onChange={e => setForm({ ...form, locationInfo: e.target.value })} fullWidth />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>{editData ? 'Update' : 'Create'}</Button>
                </DialogActions>
            </Dialog>

            <HolidayImportDialog
                open={importDialogOpen}
                onClose={() => setImportDialogOpen(false)}
                onSuccess={loadData}
                existingHolidays={allHolidays}
            />
        </>
    );
};

// ===================== WEEK OFF TAB =====================
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKS = [1, 2, 3, 4, 5];

const WeekOffTab: FC = () => {
    const dispatch = useAppDispatch();
    const [weekOffs, setWeekOffs] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editData, setEditData] = useState<any>(null);
    const [form, setForm] = useState<{ name: string; description: string; weekOffDays: { day: string; weeks: number[] }[] }>({
        name: '', description: '', weekOffDays: []
    });

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await dispatch(fetchWeekOffs()).unwrap();
            setWeekOffs(res?.data || res || []);
        } catch { setWeekOffs([]); }
        setLoading(false);
    };

    useEffect(() => { loadData(); }, []);

    const handleOpen = (data?: any) => {
        if (data) {
            setEditData(data);
            setForm({ name: data.name, description: data.description || '', weekOffDays: data.weekOffDays || data.weekOffs || [] });
        } else {
            setEditData(null);
            setForm({ name: '', description: '', weekOffDays: [] });
        }
        setDialogOpen(true);
    };

    const toggleDay = (day: string) => {
        const exists = form.weekOffDays.find(w => w.day === day);
        if (exists) {
            setForm({ ...form, weekOffDays: form.weekOffDays.filter(w => w.day !== day) });
        } else {
            setForm({ ...form, weekOffDays: [...form.weekOffDays, { day, weeks: [1, 2, 3, 4, 5] }] });
        }
    };

    const toggleWeek = (day: string, week: number) => {
        setForm({
            ...form,
            weekOffDays: form.weekOffDays.map(w => {
                if (w.day !== day) return w;
                const weeks = w.weeks.includes(week) ? w.weeks.filter(wk => wk !== week) : [...w.weeks, week];
                return { ...w, weeks };
            })
        });
    };

    const handleSave = async () => {
        try {
            if (editData) {
                await dispatch(updateWeekOff({ id: editData._id || editData.id, data: form })).unwrap();
                toast.success('Week off updated');
            } else {
                await dispatch(createWeekOff({ ...form, isActive: true })).unwrap();
                toast.success('Week off created');
            }
            setDialogOpen(false);
            loadData();
        } catch (err: any) { toast.error(err?.message || 'Failed'); }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Delete this week off config?')) return;
        try {
            await dispatch(deleteWeekOff(id)).unwrap();
            toast.success('Deleted');
            loadData();
        } catch (err: any) { toast.error(err?.message || 'Failed'); }
    };

    const columns = [
        { field: 'name', headerName: 'Name', flex: 1, minWidth: 130 },
        { field: 'description', headerName: 'Description', flex: 1.2, minWidth: 150 },
        { field: 'weekOffDays', headerName: 'Off Days', flex: 1.5, minWidth: 200,
            renderCell: ({ row }: any) => (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {(row.weekOffDays || row.weekOffs || []).map((w: any) => (
                        <Chip key={w.day} label={`${w.day} (W${w.weeks.join(',')})`} size="small"
                            style={{ color: '#488dff', backgroundColor: '#f1f0fc', border: '1px solid #488dff' }} />
                    ))}
                </Box>
            )
        },
        { field: 'actions', headerName: 'Actions', flex: 0.7, minWidth: 110,
            renderCell: ({ row }: any) => (
                <>
                    <IconButton style={{ color: '#49a563', backgroundColor: '#edf6ef', border: '0.1px solid #49a563', height: 35, width: 35, marginRight: 10 }}
                        size="small" onClick={() => handleOpen(row)}><EditIcon style={{ fontSize: 16 }} /></IconButton>
                    <IconButton style={{ color: '#c33b3b', backgroundColor: '#fbecec', border: '0.1px solid #c33b3b', height: 35, width: 35 }}
                        size="small" onClick={() => handleDelete(row._id || row.id)}><DeleteIcon style={{ fontSize: 16 }} /></IconButton>
                </>
            )
        }
    ];

    return (
        <>
            {document.getElementById('hrms-actions') && createPortal(
                <Button startIcon={<AddIcon />} size="small" onClick={() => handleOpen()}
                    style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7 }}>
                    Add Week Off
                </Button>,
                document.getElementById('hrms-actions')!
            )}
            {!loading && weekOffs.length === 0 ? (
                <HrmsEmptyState icon={WeekendIcon} title="No Week Offs Yet" description="Configure weekly off patterns for your employees. Set up which days and weeks are off." buttonLabel="Add Week Off" onAction={() => handleOpen()} />
            ) : (
                <div style={{ height: '77vh', width: '100%', overflow: 'auto', paddingRight: 10 }}>
                    <CustomDataGrid columns={columns} rows={weekOffs} getRowId={(row: any) => row._id || row.id} loading={loading} />
                </div>
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editData ? 'Edit' : 'Add'} Week Off</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="Name" size="small" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} fullWidth />
                        <TextField label="Description" size="small" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} fullWidth />

                        <Typography variant="body2" sx={{ mt: 1, mb: -1, color: '#555' }}>Off Days</Typography>

                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                                    <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 500, color: '#666' }}>Day</th>
                                    {WEEKS.map(wk => (
                                        <th key={wk} style={{ textAlign: 'center', padding: '6px 4px', fontWeight: 500, color: '#666' }}>W{wk}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {DAYS.map(day => {
                                    const selected = form.weekOffDays.find(w => w.day === day);
                                    return (
                                        <tr key={day} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                            <td style={{ padding: '4px 8px' }}>
                                                <FormControlLabel
                                                    control={<Switch size="small" checked={!!selected} onChange={() => toggleDay(day)} />}
                                                    label={<span style={{ fontSize: 13 }}>{day}</span>}
                                                    sx={{ m: 0 }}
                                                />
                                            </td>
                                            {WEEKS.map(wk => (
                                                <td key={wk} style={{ textAlign: 'center', padding: '4px' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={!!selected?.weeks.includes(wk)}
                                                        disabled={!selected}
                                                        onChange={() => selected && toggleWeek(day, wk)}
                                                        style={{ cursor: selected ? 'pointer' : 'default', width: 15, height: 15 }}
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>{editData ? 'Update' : 'Create'}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// ===================== SHIFT PATTERN CONFIG TAB =====================
const DAYS_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const PT_COLORS: Record<string, any> = {
    weekly:  { color: '#488dff', bg: '#f1f0fc', border: '#488dff' },
    monthly: { color: '#49a563', bg: '#edf6ef', border: '#49a563' },
    yearly:  { color: '#e6a817', bg: '#fdf8e8', border: '#e6a817' },
    dynamic: { color: '#9c27b0', bg: '#f3e5f5', border: '#9c27b0' },
};

const DayChip: FC<{ label: string; selected: boolean; onClick: () => void }> = ({ label, selected, onClick }) => (
    <Chip label={label} size="small" clickable onClick={onClick}
        style={selected
            ? { color: '#fff', backgroundColor: '#488dff', border: '1px solid #488dff', fontWeight: 600 }
            : { color: '#555', backgroundColor: '#f5f5f5', border: '1px solid #ddd' }} />
);

const ShiftPatternConfigTab: FC = () => {
    const dispatch = useAppDispatch();
    const [configs, setConfigs] = useState<any[]>([]);
    const [shifts, setShifts] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [panelOpen, setPanelOpen] = useState(false);
    const [editData, setEditData] = useState<any>(null);
    const [patternType, setPatternType] = useState<'weekly' | 'monthly' | 'yearly' | 'dynamic'>('weekly');
    const [form, setForm] = useState<any>({
        title: '', defaultShift: '',
        patterns: { weekly: [], monthly: [], yearly: [], dynamic: [] }
    });

    const loadData = async () => {
        setLoading(true);
        try {
            const [cfgRes, shiftRes] = await Promise.all([
                dispatch(fetchShiftConfigs()).unwrap(),
                dispatch(fetchShifts()).unwrap(),
            ]);
            setConfigs(cfgRes?.data || cfgRes || []);
            setShifts(shiftRes?.data || shiftRes || []);
        } catch { setConfigs([]); }
        setLoading(false);
    };

    useEffect(() => { loadData(); }, []);

    const handleOpen = (data?: any) => {
        if (data) {
            setEditData(data);
            const pt = data.patterns?.dynamic?.length ? 'dynamic'
                : data.patterns?.yearly?.length ? 'yearly'
                : data.patterns?.monthly?.length ? 'monthly'
                : 'weekly';
            setPatternType(pt);
            setForm({
                title: data.title,
                defaultShift: data.defaultShift?._id || data.defaultShift || '',
                patterns: {
                    weekly: (data.patterns?.weekly || []).map((r: any) => ({
                        ...r, shift: r.shift?._id || r.shift || ''
                    })),
                    monthly: (data.patterns?.monthly || []).map((r: any) => ({
                        ...r, shift: r.shift?._id || r.shift || ''
                    })),
                    yearly: (data.patterns?.yearly || []).map((r: any) => ({
                        ...r, shift: r.shift?._id || r.shift || ''
                    })),
                    dynamic: (data.patterns?.dynamic || []).map((r: any) => ({
                        ...r, shifts: (r.shifts || []).map((s: any) => s?._id || s)
                    })),
                }
            });
        } else {
            setEditData(null);
            setPatternType('weekly');
            setForm({ title: '', defaultShift: '', patterns: { weekly: [], monthly: [], yearly: [], dynamic: [] } });
        }
        setPanelOpen(true);
    };

    const handleClose = () => { setPanelOpen(false); setEditData(null); };

    const handleSave = async () => {
        const payload = {
            ...form,
            title: (form.title || '').trim(),
            defaultShift: (form.defaultShift || '').trim(),
            patterns: normalizePatterns(form.patterns)
        };
        if (!payload.title) { toast.error('Title is required'); return; }
        if (!payload.defaultShift) { toast.error('Default Shift is required'); return; }
        const hasAnyPattern = payload.patterns.weekly.length || payload.patterns.monthly.length ||
            payload.patterns.yearly.length || payload.patterns.dynamic.length;
        if (!hasAnyPattern) { toast.error('Add at least one valid pattern row'); return; }

        setSaving(true);
        try {
            if (editData) {
                await dispatch(updateShiftConfig({ id: editData._id || editData.id, data: payload })).unwrap();
                toast.success('Shift pattern updated');
            } else {
                await dispatch(createShiftConfig({ ...payload, isActive: true })).unwrap();
                toast.success('Shift pattern created');
            }
            handleClose();
            loadData();
        } catch (err: any) { toast.error(err?.message || 'Failed'); }
        setSaving(false);
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Delete this shift pattern config?')) return;
        try {
            await dispatch(deleteShiftConfig(id)).unwrap();
            toast.success('Deleted');
            loadData();
        } catch (err: any) { toast.error(err?.message || 'Failed'); }
    };

    const addRow = (type: string) => {
        const blank: any = { weekly: { days: [], shift: '' }, monthly: { weeks: [], days: [], shift: '' }, yearly: { months: [], weeks: [], days: [], shift: '' }, dynamic: { type: 'daily', cycleCount: 1, shifts: [] } };
        setForm((f: any) => ({ ...f, patterns: { ...f.patterns, [type]: [...f.patterns[type], blank[type]] } }));
    };

    const updateRow = (type: string, index: number, key: string, value: any) => {
        setForm((f: any) => {
            const arr = [...f.patterns[type]];
            arr[index] = { ...arr[index], [key]: value };
            return { ...f, patterns: { ...f.patterns, [type]: arr } };
        });
    };

    const removeRow = (type: string, index: number) => {
        setForm((f: any) => ({ ...f, patterns: { ...f.patterns, [type]: f.patterns[type].filter((_: any, i: number) => i !== index) } }));
    };

    const toggleItem = (arr: any[], item: any) =>
        arr.includes(item) ? arr.filter((x: any) => x !== item) : [...arr, item];

    const normalizePatterns = (patterns: any) => {
        const toId = (v: any) => (typeof v === 'string' ? v.trim() : v);
        return {
            weekly: (patterns.weekly || []).map((r: any) => ({ ...r, days: r.days || [], shift: toId(r.shift) })).filter((r: any) => r.shift && r.days.length),
            monthly: (patterns.monthly || []).map((r: any) => ({ ...r, weeks: r.weeks || [], days: r.days || [], shift: toId(r.shift) })).filter((r: any) => r.shift && r.weeks.length && r.days.length),
            yearly: (patterns.yearly || []).map((r: any) => ({ ...r, months: r.months || [], weeks: r.weeks || [], days: r.days || [], shift: toId(r.shift) })).filter((r: any) => r.shift && r.months.length && r.weeks.length && r.days.length),
            dynamic: (patterns.dynamic || []).map((r: any) => ({ ...r, type: r.type || 'daily', cycleCount: Number(r.cycleCount || 1), shifts: (r.shifts || []).map(toId).filter(Boolean) })).filter((r: any) => r.cycleCount > 0 && r.shifts.length),
        };
    };

    const getPatternSummary = (row: any) => {
        const p = row.patterns || {};
        if (p.dynamic?.length) {
            const r = p.dynamic[0];
            const names = (r.shifts || []).map((s: any) => s?.name || s).filter(Boolean).join(', ');
            return `Every ${r.cycleCount} ${r.type}(s): ${names || '-'}`;
        }
        if (p.yearly?.length) {
            const r = p.yearly[0];
            return `${(r.months || []).join(', ')} · ${(r.days || []).join(', ')} → ${r.shift?.name || '-'}`;
        }
        if (p.monthly?.length) {
            const r = p.monthly[0];
            return `${(r.weeks || []).map((w: number) => `W${w}`).join(', ')} · ${(r.days || []).join(', ')} → ${r.shift?.name || '-'}`;
        }
        if (p.weekly?.length) {
            return p.weekly.map((r: any) => `${(r.days || []).join(', ')} → ${r.shift?.name || '-'}`).join(' | ');
        }
        return '-';
    };

    const columns = [
        { field: 'title', headerName: 'Title', flex: 1, minWidth: 130 },
        { field: 'defaultShift', headerName: 'Default Shift', flex: 0.8, minWidth: 110,
            renderCell: ({ row }: any) => row.defaultShift?.name || row.defaultShift || '-'
        },
        { field: 'patternType', headerName: 'Type', flex: 0.6, minWidth: 90,
            renderCell: ({ row }: any) => {
                const p = row.patterns || {};
                const pt = p.dynamic?.length ? 'dynamic' : p.yearly?.length ? 'yearly' : p.monthly?.length ? 'monthly' : p.weekly?.length ? 'weekly' : null;
                if (!pt) return <span style={{ color: '#aaa', fontSize: 12 }}>-</span>;
                const c = PT_COLORS[pt];
                return <Chip label={pt.charAt(0).toUpperCase() + pt.slice(1)} size="small"
                    style={{ color: c.color, backgroundColor: c.bg, border: `1px solid ${c.border}` }} />;
            }
        },
        { field: 'patternSummary', headerName: 'Pattern Summary', flex: 1.8, minWidth: 200,
            renderCell: ({ row }: any) => <span style={{ fontSize: 12, color: '#555' }}>{getPatternSummary(row)}</span>
        },
        { field: 'actions', headerName: 'Actions', flex: 0.7, minWidth: 110,
            renderCell: ({ row }: any) => (
                <>
                    <IconButton style={{ color: '#49a563', backgroundColor: '#edf6ef', border: '0.1px solid #49a563', height: 35, width: 35, marginRight: 10 }}
                        size="small" onClick={() => handleOpen(row)}><EditIcon style={{ fontSize: 16 }} /></IconButton>
                    <IconButton style={{ color: '#c33b3b', backgroundColor: '#fbecec', border: '0.1px solid #c33b3b', height: 35, width: 35 }}
                        size="small" onClick={() => handleDelete(row._id || row.id)}><DeleteIcon style={{ fontSize: 16 }} /></IconButton>
                </>
            )
        }
    ];

    const ptLabels: Record<string, string> = { weekly: 'Weekly', monthly: 'Monthly', yearly: 'Yearly', dynamic: 'Dynamic' };
    const ptDescriptions: Record<string, string> = {
        weekly: 'Fixed shift for specific days every week',
        monthly: 'Shift based on week number & day within a month',
        yearly: 'Shift based on month, week & day combination',
        dynamic: 'Rotating shift cycle (e.g. 2-day A then 2-day B)',
    };

    return (
        <>
            {document.getElementById('hrms-actions') && createPortal(
                <Button startIcon={<AddIcon />} size="small" onClick={() => handleOpen()}
                    style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7 }}>
                    Add Shift Pattern
                </Button>,
                document.getElementById('hrms-actions')!
            )}

            {!loading && configs.length === 0 ? (
                <HrmsEmptyState icon={LoopIcon} title="No Shift Patterns Yet" description="Create shift rotation patterns for your employees. Set up weekly, monthly, or custom rotation schedules." buttonLabel="Add Shift Pattern" onAction={() => handleOpen()} />
            ) : (
                <div style={{ height: '77vh', width: '100%', overflow: 'auto', paddingRight: 10 }}>
                    <CustomDataGrid columns={columns} rows={configs} getRowId={(row: any) => row._id || row.id} loading={loading} />
                </div>
            )}

            {/* DRAWER */}
            <Drawer
                anchor="right"
                open={panelOpen}
                onClose={handleClose}
                PaperProps={{ sx: { width: { xs: '100%', sm: 580 }, display: 'flex', flexDirection: 'column' } }}
            >
                {/* Header */}
                <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {editData ? 'Edit' : 'Add'} Shift Pattern Config
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        Define how shifts rotate for employees
                    </Typography>
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                    <Grid container spacing={3}>

                        {/* Title + Default Shift */}
                        <Grid item xs={12} sm={6}>
                            <TextField label="Config Title" size="small" fullWidth value={form.title}
                                onChange={e => setForm({ ...form, title: e.target.value })}
                                placeholder="e.g. Standard Office, Night Rotation" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Default Shift" size="small" select fullWidth value={form.defaultShift}
                                onChange={e => setForm({ ...form, defaultShift: e.target.value })}
                                helperText="Applied when no rule matches a day">
                                <MenuItem value=""><em>Select shift</em></MenuItem>
                                {shifts.map((s: any) => (
                                    <MenuItem key={s._id} value={s._id}>
                                        <Box>
                                            <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{s.name}</Typography>
                                            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{s.shiftCode} · {s.shiftStartTime}–{s.shiftEndTime}</Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {/* Pattern Type cards */}
                        <Grid item xs={12}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1.5 }}>Pattern Type</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 1 }}>
                                {(['weekly', 'monthly', 'yearly', 'dynamic'] as const).map(pt => {
                                    const c = PT_COLORS[pt];
                                    const active = patternType === pt;
                                    return (
                                        <Box key={pt} onClick={() => setPatternType(pt)} sx={{
                                            border: `1.5px solid ${active ? c.border : '#e5e7eb'}`,
                                            backgroundColor: active ? c.bg : '#f9fafb',
                                            borderRadius: 2, p: 1.5, cursor: 'pointer', transition: 'all 0.15s',
                                            '&:hover': { borderColor: c.border, backgroundColor: c.bg }
                                        }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: active ? c.color : '#374151' }}>{ptLabels[pt]}</Typography>
                                            <Typography sx={{ fontSize: 10, color: '#9ca3af', mt: 0.3, lineHeight: 1.3 }}>{ptDescriptions[pt]}</Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Grid>

                        {/* Pattern Rules */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                    {ptLabels[patternType]} Rules
                                </Typography>
                                <Button size="small" startIcon={<AddIcon />} variant="outlined" onClick={() => addRow(patternType)}>
                                    Add Rule
                                </Button>
                            </Box>

                            {form.patterns[patternType].length === 0 && (
                                <Box sx={{ border: '1.5px dashed #e5e7eb', borderRadius: 2, p: 3, textAlign: 'center', backgroundColor: '#f9fafb' }}>
                                    <Typography variant="body2" sx={{ color: '#9ca3af' }}>No rules yet — click "Add Rule" to start</Typography>
                                </Box>
                            )}

                            {patternType === 'weekly' && form.patterns.weekly.map((row: any, i: number) => (
                                <Paper key={i} variant="outlined" sx={{ p: 2, mb: 1.5, borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Rule {i + 1}</Typography>
                                        <IconButton size="small" color="error" onClick={() => removeRow('weekly', i)}><DeleteIcon fontSize="small" /></IconButton>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">Apply on days</Typography>
                                    <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mt: 0.8, mb: 1.5 }}>
                                        {DAYS_SHORT.map(d => <DayChip key={d} label={d} selected={row.days.includes(d)} onClick={() => updateRow('weekly', i, 'days', toggleItem(row.days, d))} />)}
                                    </Box>
                                    <TextField label="Assign Shift" size="small" select fullWidth value={row.shift}
                                        onChange={e => updateRow('weekly', i, 'shift', e.target.value)}>
                                        <MenuItem value=""><em>Select shift</em></MenuItem>
                                        {shifts.map((s: any) => <MenuItem key={s._id} value={s._id}>{s.name} ({s.shiftStartTime}–{s.shiftEndTime})</MenuItem>)}
                                    </TextField>
                                </Paper>
                            ))}

                            {patternType === 'monthly' && form.patterns.monthly.map((row: any, i: number) => (
                                <Paper key={i} variant="outlined" sx={{ p: 2, mb: 1.5, borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Rule {i + 1}</Typography>
                                        <IconButton size="small" color="error" onClick={() => removeRow('monthly', i)}><DeleteIcon fontSize="small" /></IconButton>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">Week of month</Typography>
                                    <Box sx={{ display: 'flex', gap: 0.8, mt: 0.8, mb: 1.5 }}>
                                        {[1,2,3,4,5].map(w => <DayChip key={w} label={`W${w}`} selected={row.weeks.includes(w)} onClick={() => updateRow('monthly', i, 'weeks', toggleItem(row.weeks, w))} />)}
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">Apply on days</Typography>
                                    <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mt: 0.8, mb: 1.5 }}>
                                        {DAYS_SHORT.map(d => <DayChip key={d} label={d} selected={row.days.includes(d)} onClick={() => updateRow('monthly', i, 'days', toggleItem(row.days, d))} />)}
                                    </Box>
                                    <TextField label="Assign Shift" size="small" select fullWidth value={row.shift}
                                        onChange={e => updateRow('monthly', i, 'shift', e.target.value)}>
                                        <MenuItem value=""><em>Select shift</em></MenuItem>
                                        {shifts.map((s: any) => <MenuItem key={s._id} value={s._id}>{s.name} ({s.shiftStartTime}–{s.shiftEndTime})</MenuItem>)}
                                    </TextField>
                                </Paper>
                            ))}

                            {patternType === 'yearly' && form.patterns.yearly.map((row: any, i: number) => (
                                <Paper key={i} variant="outlined" sx={{ p: 2, mb: 1.5, borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Rule {i + 1}</Typography>
                                        <IconButton size="small" color="error" onClick={() => removeRow('yearly', i)}><DeleteIcon fontSize="small" /></IconButton>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">Months</Typography>
                                    <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mt: 0.8, mb: 1.5 }}>
                                        {MONTHS_SHORT.map(m => <DayChip key={m} label={m} selected={row.months.includes(m)} onClick={() => updateRow('yearly', i, 'months', toggleItem(row.months, m))} />)}
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">Week of month</Typography>
                                    <Box sx={{ display: 'flex', gap: 0.8, mt: 0.8, mb: 1.5 }}>
                                        {[1,2,3,4,5].map(w => <DayChip key={w} label={`W${w}`} selected={row.weeks.includes(w)} onClick={() => updateRow('yearly', i, 'weeks', toggleItem(row.weeks, w))} />)}
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">Apply on days</Typography>
                                    <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mt: 0.8, mb: 1.5 }}>
                                        {DAYS_SHORT.map(d => <DayChip key={d} label={d} selected={row.days.includes(d)} onClick={() => updateRow('yearly', i, 'days', toggleItem(row.days, d))} />)}
                                    </Box>
                                    <TextField label="Assign Shift" size="small" select fullWidth value={row.shift}
                                        onChange={e => updateRow('yearly', i, 'shift', e.target.value)}>
                                        <MenuItem value=""><em>Select shift</em></MenuItem>
                                        {shifts.map((s: any) => <MenuItem key={s._id} value={s._id}>{s.name} ({s.shiftStartTime}–{s.shiftEndTime})</MenuItem>)}
                                    </TextField>
                                </Paper>
                            ))}

                            {patternType === 'dynamic' && form.patterns.dynamic.map((row: any, i: number) => (
                                <Paper key={i} variant="outlined" sx={{ p: 2, mb: 1.5, borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Cycle {i + 1}</Typography>
                                        <IconButton size="small" color="error" onClick={() => removeRow('dynamic', i)}><DeleteIcon fontSize="small" /></IconButton>
                                    </Box>
                                    <Grid container spacing={2} sx={{ mb: 1.5 }}>
                                        <Grid item xs={7}>
                                            <TextField label="Cycle Type" size="small" select fullWidth value={row.type}
                                                onChange={e => updateRow('dynamic', i, 'type', e.target.value)}>
                                                {['daily', 'weekly', 'monthly', 'yearly'].map(t => (
                                                    <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={5}>
                                            <TextField label="Repeat every N" size="small" type="number" fullWidth
                                                value={row.cycleCount || 1} inputProps={{ min: 1 }}
                                                onChange={e => updateRow('dynamic', i, 'cycleCount', Math.max(1, Number(e.target.value)))} />
                                        </Grid>
                                    </Grid>
                                    <Typography variant="caption" color="text.secondary">Shifts in rotation order</Typography>
                                    <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mt: 0.8 }}>
                                        {shifts.map((s: any) => {
                                            const selected = row.shifts.includes(s._id);
                                            return (
                                                <Chip key={s._id} label={s.name} size="small" clickable
                                                    onClick={() => updateRow('dynamic', i, 'shifts', toggleItem(row.shifts, s._id))}
                                                    style={selected
                                                        ? { color: '#fff', backgroundColor: '#9c27b0', border: '1px solid #9c27b0', fontWeight: 600 }
                                                        : { color: '#555', backgroundColor: '#f5f5f5', border: '1px solid #ddd' }} />
                                            );
                                        })}
                                    </Box>
                                    {row.shifts.length > 0 && (
                                        <Typography variant="caption" sx={{ color: '#9c27b0', mt: 0.8, display: 'block' }}>
                                            Order: {row.shifts.map((id: string) => shifts.find((s: any) => s._id === id)?.name || id).join(' → ')}
                                        </Typography>
                                    )}
                                </Paper>
                            ))}
                        </Grid>
                    </Grid>
                </Box>

                {/* Footer */}
                <Box sx={{ borderTop: '1px solid #e5e7eb', p: 2, backgroundColor: '#f9fafb', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button variant="outlined" onClick={handleClose} sx={{ minWidth: 100 }}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ minWidth: 140 }}>
                        {saving ? 'Saving...' : editData ? 'Update Config' : 'Save Config'}
                    </Button>
                </Box>
            </Drawer>
        </>
    );
};

// ===================== EMPLOYMENT ASSIGNMENT TAB =====================
const EmploymentAssignmentTab: FC = () => {
    const dispatch = useAppDispatch();
    const { selectedAccount } = useAppSelector(state => state.auth);
    const [employees, setEmployees] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize] = useState(10);
    const [shiftPatterns, setShiftPatterns] = useState<any[]>([]);
    const [weekOffs, setWeekOffs] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [selectedEmp, setSelectedEmp] = useState<any>(null);
    const [panelOpen, setPanelOpen] = useState(false);
    const [assignShift, setAssignShift] = useState('');
    const [assignWeekOff, setAssignWeekOff] = useState('');
    const [saving, setSaving] = useState(false);

    const loadEmployees = async (pg: number, filter: string) => {
        setLoading(true);
        try {
            const res = await dispatch(fetchEmployeesByAccount({
                accountId: selectedAccount._id, page: pg + 1, limit: pageSize, filter
            })).unwrap();
            setEmployees(res?.employees || res?.data || res || []);
            setTotal(res?.total || 0);
        } catch { setEmployees([]); }
        setLoading(false);
    };

    const loadMeta = async () => {
        try {
            const [spRes, woRes] = await Promise.all([
                dispatch(fetchShiftConfigs()).unwrap(),
                dispatch(fetchWeekOffs()).unwrap(),
            ]);
            setShiftPatterns(spRes?.data || spRes || []);
            setWeekOffs(woRes?.data || woRes || []);
        } catch {}
    };

    useEffect(() => { loadMeta(); }, [selectedAccount]);
    useEffect(() => { loadEmployees(page, search); }, [page, search, selectedAccount]);

    const handleSearch = () => {
        setPage(0);
        setSearch(searchInput);
    };

    const resolveAssignedId = (value: any) => {
        const resolved = value?._id ?? value?.id ?? value ?? '';
        const str = String(resolved);
        return str && str !== 'null' && str !== 'undefined' ? str : '';
    };

    const handleAssignClick = (emp: any) => {
        setSelectedEmp(emp);
        setAssignShift(resolveAssignedId(emp.shiftPatternConfig));
        setAssignWeekOff(resolveAssignedId(emp.weekOffPattern));
        setPanelOpen(true);
    };

    const handleSave = async () => {
        if (!selectedEmp) return;
        setSaving(true);
        try {
            const validShift = assignShift && assignShift !== 'undefined' && assignShift !== 'null';
            const validWeekOff = assignWeekOff && assignWeekOff !== 'undefined' && assignWeekOff !== 'null';
            if (!validShift && !validWeekOff) {
                toast.info('Nothing to assign');
                setSaving(false);
                return;
            }
            if (validShift) {
                await dispatch(assignShiftPatternToEmployee({
                    employeeId: selectedEmp._id,
                    configId: assignShift
                })).unwrap();
            }
            if (validWeekOff) {
                await dispatch(assignWeekOffToEmployee({
                    employeeId: selectedEmp._id,
                    weekOffId: assignWeekOff
                })).unwrap();
            }

            toast.success('Assigned successfully');
            await loadEmployees(page, search);
            setPanelOpen(false);
        } catch (err: any) { toast.error(err?.message || 'Failed'); }
        setSaving(false);
    };

    const shiftPatternName = (val: any) => {
        if (val?.title) return val.title;
        const id = (val?._id || val || '').toString();
        return shiftPatterns.find((s: any) => s._id?.toString() === id)?.title || '-';
    };
    const weekOffName = (val: any) => {
        if (val?.name) return val.name;
        const id = (val?._id || val?.id || val || '').toString();
        return weekOffs.find((w: any) => (w._id || w.id)?.toString() === id)?.name || '-';
    };

    const columns = [
        { field: 'empId', headerName: 'Emp ID', flex: 0.6, minWidth: 90 },
        { field: 'name', headerName: 'Name', flex: 1.2, minWidth: 140 },
        { field: 'shiftPatternConfig', headerName: 'Shift Pattern', flex: 1, minWidth: 130,
            renderCell: ({ row }: any) => {
                const name = shiftPatternName(row.shiftPatternConfig);
                return name !== '-'
                    ? <Chip label={name} size="small" style={{ color: '#49a563', backgroundColor: '#edf6ef', border: '1px solid #49a563' }} />
                    : <span style={{ color: '#aaa', fontSize: 12 }}>Not assigned</span>;
            }
        },
        { field: 'weekOffPattern', headerName: 'Week Off Pattern', flex: 1, minWidth: 130,
            renderCell: ({ row }: any) => {
                const name = weekOffName(row.weekOffPattern);
                return name !== '-'
                    ? <Chip label={name} size="small" style={{ color: '#488dff', backgroundColor: '#f1f0fc', border: '1px solid #488dff' }} />
                    : <span style={{ color: '#aaa', fontSize: 12 }}>Not assigned</span>;
            }
        },
        { field: 'actions', headerName: 'Actions', flex: 0.6, minWidth: 100,
            renderCell: ({ row }: any) => (
                <Button size="small" variant="outlined" onClick={() => handleAssignClick(row)}
                    style={{ borderRadius: 6, height: 30, fontSize: 12 }}>
                    Assign
                </Button>
            )
        }
    ];

    return (
        <>
            {document.getElementById('hrms-actions') && createPortal(
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        size="small"
                        placeholder="Search employees..."
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSearch()}
                        style={{ width: 220 }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                        }}
                    />
                    <Button size="small" variant="outlined" onClick={handleSearch}
                        style={{ color: '#488dff', borderColor: '#488dff', height: 35, borderRadius: 7 }}>
                        Search
                    </Button>
                </Box>,
                document.getElementById('hrms-actions')!
            )}

            {!loading && employees.length === 0 ? (
                <HrmsEmptyState icon={AssignmentIndIcon} title="No Assignments Yet" description="Assign shift patterns and week offs to your employees. Manage employee work schedules from here." />
            ) : (
                <div style={{ height: '77vh', width: '100%', overflow: 'auto', paddingRight: 10 }}>
                    <CustomDataGrid
                        columns={columns}
                        rows={employees}
                        getRowId={(row: any) => row._id || row.id}
                        loading={loading}
                        rowCount={total}
                        page={page}
                        pageSize={pageSize}
                        onPageChange={(newPage: number) => setPage(newPage)}
                    />
                </div>
            )}

            {/* ASSIGN DRAWER */}
            <Drawer
                anchor="right"
                open={panelOpen}
                onClose={() => setPanelOpen(false)}
                PaperProps={{ sx: { width: { xs: '100%', sm: 500 }, display: 'flex', flexDirection: 'column' } }}
            >
                {/* Header */}
                <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>Assign Work Patterns</Typography>
                    {selectedEmp && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                            {selectedEmp.name} · {selectedEmp.empId}
                        </Typography>
                    )}
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                    <Grid container spacing={3}>

                        {/* Employee info card */}
                        {selectedEmp && (
                            <Grid item xs={12}>
                                <Box sx={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 2, p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box sx={{ width: 42, height: 42, borderRadius: '50%', backgroundColor: '#488dff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 17 }}>
                                            {(selectedEmp.name || '?')[0].toUpperCase()}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{selectedEmp.name}</Typography>
                                        <Box sx={{ display: 'flex', gap: 0.8, mt: 0.5, flexWrap: 'wrap' }}>
                                            <Chip label={selectedEmp.empId || 'No ID'} size="small"
                                                style={{ fontSize: 11, height: 20, color: '#488dff', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }} />
                                            {selectedEmp.department && <Chip label={selectedEmp.department} size="small" style={{ fontSize: 11, height: 20 }} />}
                                            {selectedEmp.designation && <Chip label={selectedEmp.designation} size="small" style={{ fontSize: 11, height: 20 }} />}
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>
                        )}

                        {/* Current assignments */}
                        {selectedEmp && (shiftPatternName(selectedEmp.shiftPatternConfig) !== '-' || weekOffName(selectedEmp.weekOffPattern) !== '-') && (
                            <Grid item xs={12}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>Current Assignment</Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {shiftPatternName(selectedEmp.shiftPatternConfig) !== '-' && (
                                        <Chip label={`Shift: ${shiftPatternName(selectedEmp.shiftPatternConfig)}`} size="small"
                                            style={{ color: '#49a563', backgroundColor: '#edf6ef', border: '1px solid #49a563' }} />
                                    )}
                                    {weekOffName(selectedEmp.weekOffPattern) !== '-' && (
                                        <Chip label={`Week Off: ${weekOffName(selectedEmp.weekOffPattern)}`} size="small"
                                            style={{ color: '#488dff', backgroundColor: '#eff6ff', border: '1px solid #488dff' }} />
                                    )}
                                </Box>
                                <Divider sx={{ mt: 2 }} />
                            </Grid>
                        )}

                        {/* Shift Pattern dropdown */}
                        <Grid item xs={12}>
                            <TextField
                                label="Shift Pattern Config"
                                size="small"
                                select
                                fullWidth
                                value={assignShift || ''}
                                onChange={e => setAssignShift(e.target.value)}
                                helperText="Defines which shift applies on each day / rotation"
                            >
                                <MenuItem value=""><em>— None —</em></MenuItem>
                                {shiftPatterns.map((sp: any) => {
                                    const p = sp.patterns || {};
                                    const pt = p.dynamic?.length ? 'Dynamic' : p.yearly?.length ? 'Yearly' : p.monthly?.length ? 'Monthly' : p.weekly?.length ? 'Weekly' : '';
                                    return (
                                        <MenuItem key={String(sp._id)} value={String(sp._id)}>
                                            <Box>
                                                <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{sp.title}</Typography>
                                                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                                                    {pt && `${pt} pattern`}{sp.defaultShift?.name && ` · Default: ${sp.defaultShift.name}`}
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    );
                                })}
                            </TextField>
                        </Grid>

                        {/* Week Off dropdown */}
                        <Grid item xs={12}>
                            <TextField
                                label="Week Off Pattern"
                                size="small"
                                select
                                fullWidth
                                value={assignWeekOff || ''}
                                onChange={e => setAssignWeekOff(e.target.value)}
                                helperText="Defines which days are weekly holidays"
                            >
                                <MenuItem value=""><em>— None —</em></MenuItem>
                                {weekOffs.map((wo: any) => {
                                    const id = String(wo._id || wo.id);
                                    const days = (wo.weekOffDays || wo.weekOffs || []).map((w: any) => w.day).join(', ');
                                    return (
                                        <MenuItem key={id} value={id}>
                                            <Box>
                                                <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{wo.name}</Typography>
                                                {days && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Off: {days}</Typography>}
                                            </Box>
                                        </MenuItem>
                                    );
                                })}
                            </TextField>
                        </Grid>

                    </Grid>
                </Box>

                {/* Footer */}
                <Box sx={{ borderTop: '1px solid #e5e7eb', p: 2, backgroundColor: '#f9fafb', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button variant="outlined" onClick={() => setPanelOpen(false)} sx={{ minWidth: 100 }}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ minWidth: 140 }}>
                        {saving ? 'Saving...' : 'Save Assignment'}
                    </Button>
                </Box>
            </Drawer>
        </>
    );
};

// ===================== WORK LOCATION TAB =====================
const WorkLocationTab: FC = () => {
    const dispatch = useAppDispatch();
    const { selectedAccount } = useAppSelector(state => state.auth);
    const accountId = selectedAccount?._id;

    const [addresses, setAddresses] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editAddressId, setEditAddressId] = useState<string | undefined>(undefined);

    const loadAddresses = async () => {
        if (!accountId) return;
        setLoading(true);
        try {
            const res: any = await dispatch(fetchAddresses(accountId)).unwrap();
            const list = res?.addresses || res?.data || res || [];
            setAddresses(Array.isArray(list) ? list : []);
        } catch {
            setAddresses([]);
        }
        setLoading(false);
    };

    useEffect(() => { loadAddresses(); }, [accountId]);

    const handleOpenAdd = () => {
        setEditAddressId(undefined);
        setDialogOpen(true);
    };

    const handleOpenEdit = (id: string) => {
        setEditAddressId(id);
        setDialogOpen(true);
    };

    const handleClose = (changed: any) => {
        setDialogOpen(false);
        setEditAddressId(undefined);
        if (changed) loadAddresses();
    };

    const formatAddress = (addr: any) => [addr.line1, addr.line2, addr.line3, addr.city, addr.state?.name || addr.state, addr.country?.name || addr.country, addr.code]
        .filter(Boolean).join(', ');

    const columns = [
        { field: 'title', headerName: 'Title', flex: 1, minWidth: 150, renderCell: ({ value }: any) => value || '-' },
        { field: 'type', headerName: 'Type', flex: 0.6, minWidth: 100, renderCell: ({ value }: any) => value || '-' },
        {
            field: 'address',
            headerName: 'Address',
            flex: 1.6,
            minWidth: 240,
            renderCell: ({ row }: any) => {
                const addr = formatAddress(row);
                return (
                    <Tooltip title={addr || ''} placement="top" arrow>
                        <Typography sx={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: addr ? 'inherit' : 'text.secondary' }}>
                            {addr || 'No address'}
                        </Typography>
                    </Tooltip>
                );
            }
        },
        { field: 'latitude', headerName: 'Latitude', flex: 0.8, minWidth: 100, renderCell: ({ value }: any) => value != null && value !== '' ? Number(value).toFixed(6) : '-' },
        { field: 'longitude', headerName: 'Longitude', flex: 0.8, minWidth: 100, renderCell: ({ value }: any) => value != null && value !== '' ? Number(value).toFixed(6) : '-' },
        { field: 'radius', headerName: 'Radius (m)', flex: 0.6, minWidth: 90, renderCell: ({ value }: any) => value != null ? value : '-' },
        {
            field: 'actions', headerName: 'Actions', flex: 0.7, minWidth: 110,
            renderCell: ({ row }: any) => (
                <IconButton style={{ color: '#49a563', backgroundColor: '#edf6ef', border: '0.1px solid #49a563', height: 35, width: 35 }}
                    size="small" onClick={() => handleOpenEdit(row._id || row.id)}><EditIcon style={{ fontSize: 16 }} /></IconButton>
            )
        }
    ];

    return (
        <>
            {document.getElementById('hrms-actions') && createPortal(
                <Button startIcon={<AddIcon />} size="small" onClick={handleOpenAdd}
                    style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7 }}>
                    Add Address
                </Button>,
                document.getElementById('hrms-actions')!
            )}
            {!loading && addresses.length === 0 ? (
                <HrmsEmptyState icon={LocationOnIcon} title="No Addresses Yet" description="Add business addresses with latitude, longitude and radius. Used for geo-tagging attendance." buttonLabel="Add Address" onAction={handleOpenAdd} />
            ) : (
                <div style={{ height: '77vh', width: '100%', overflow: 'auto', paddingRight: 10 }}>
                    <CustomDataGrid columns={columns} rows={addresses} getRowId={(row: any) => row._id || row.id} loading={loading} />
                </div>
            )}

            <DialogAddressForm
                open={dialogOpen}
                onClose={handleClose}
                addressId={editAddressId}
            />
        </>
    );
};

// ===================== MAIN HRMS CONFIG PAGE =====================
const HrmsConfig: FC = () => {
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(() => {
        const t = searchParams.get('tab');
        return t !== null && !isNaN(Number(t)) ? Number(t) : 0;
    });

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, gap: 2 }}>
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" sx={{ minWidth: 0, flex: 1 }}>
                    <Tab label="Leave Config" />
                    <Tab label="Shifts" />
                    <Tab label="Holidays" />
                    <Tab label="Week Offs" />
                    <Tab label="Shift Pattern Config" />
                    <Tab label="Employment Assignment" />
                    <Tab label="Work Locations" />
                </Tabs>
                <Box sx={{ display: 'flex', gap: 2, mr: '10px', flexShrink: 0, alignItems: 'center', whiteSpace: 'nowrap', '& .MuiButton-root': { whiteSpace: 'nowrap', flexShrink: 0 } }} id="hrms-actions" />
            </Box>
            {activeTab === 0 && <LeaveConfigTab />}
            {activeTab === 1 && <ShiftTab />}
            {activeTab === 2 && <HolidayTab />}
            {activeTab === 3 && <WeekOffTab />}
            {activeTab === 4 && <ShiftPatternConfigTab />}
            {activeTab === 5 && <EmploymentAssignmentTab />}
            {activeTab === 6 && <WorkLocationTab />}
        </>
    );
};

export default HrmsConfig;
